import {Editor, Extension, isNodeSelection, isTextSelection, posToDOMRect} from '@tiptap/core'
import {EditorState, Plugin, PluginKey} from "prosemirror-state";
import tippy, {GetReferenceClientRect, Instance, Props} from "tippy.js";
import {EditorView} from "prosemirror-view";

export interface UpdateHtmlElement {
    (element: HTMLElement): void;
}

export interface CreateHtmlElement {
    (): HTMLElement;
}

export type BubbleLikeMenuOptions = Omit<BubbleLikeMenuPluginProps, 'editor' | 'element'> & {}

export const BubbleLikeMenu = Extension.create<BubbleLikeMenuOptions>({
    name: 'bubbleLikeMenu',

    defaultOptions: {
        tippyOptions: {},
        key: 'bubbleLikeMenu',
        shouldShow: null,
        updateContent: null,
        createContent: null
    },

    addProseMirrorPlugins() {
        return [
            BubbleLikeMenuPlugin({
                key: this.options.key,
                editor: this.editor,
                tippyOptions: this.options.tippyOptions,
                shouldShow: this.options.shouldShow,
                updateContent: this.options.updateContent,
                createContent: this.options.createContent
            }),
        ]
    },
})

export interface BubbleLikeMenuPluginProps {
    key: PluginKey | string,
    editor: Editor,
    tippyOptions?: Partial<Props>,
    shouldShow: ((props: {
        editor: Editor,
        view: EditorView,
        state: EditorState,
        oldState?: EditorState,
        from: number,
        to: number,
    }) => boolean) | null,
    updateContent: UpdateHtmlElement | null,
    createContent: CreateHtmlElement | null
}

export type BubbleLikeMenuViewProps = BubbleLikeMenuPluginProps & {
    view: EditorView,
}

export class BubbleLikeMenuView {
    public editor: Editor
    public view: EditorView
    public preventHide = false

    private readonly updateContent: UpdateHtmlElement | null

    private readonly tippyOptions: any
    private tippyGetReferenceClientRect: GetReferenceClientRect | null = null
    private tippy: Instance | null = null

    public shouldShow: Exclude<BubbleLikeMenuPluginProps['shouldShow'], null> = ({state, from, to}) => {
        const {doc, selection} = state
        const {empty} = selection

        // Sometime check for `empty` is not enough.
        // Doubleclick an empty paragraph returns a node size of 2.
        // So we check also for an empty text size.
        const isEmptyTextBlock = !doc.textBetween(from, to).length
            && isTextSelection(state.selection)

        return !(empty || isEmptyTextBlock);
    }

    constructor({
                    editor,
                    view,
                    tippyOptions,
                    shouldShow,
                    updateContent,
                    createContent
                }: BubbleLikeMenuViewProps) {
        this.editor = editor
        this.view = view
        this.updateContent = updateContent

        this.tippyOptions = {
            duration: 0,
            interactive: true,
            trigger: 'manual',
            placement: 'top',
            hideOnClick: 'toggle',
            ...tippyOptions
        };

        if (createContent) {
            this.tippyOptions.content = createContent
        }

        const onShown = this.tippyOptions.onShown
        this.tippyOptions.onShown = (instance: Instance) => {
            if (onShown) onShown(instance);
            instance.popper.addEventListener('mousedown', this.mousedownHandler, {capture: true})
            this.updateContentSafe(instance.popper)
        }

        const onHide = this.tippyOptions.onHide
        this.tippyOptions.onHide = (instance: Instance) => {
            if (onHide) onHide(instance);
            instance.popper.removeEventListener('mousedown', this.mousedownHandler)
        }

        if (shouldShow) {
            this.shouldShow = shouldShow
        }

        this.view.dom.addEventListener('dragstart', this.dragstartHandler)
        this.editor.on('focus', this.focusHandler)
        this.editor.on('blur', this.blurHandler)
    }

    mousedownHandler = () => {
        this.preventHide = true
    }

    dragstartHandler = () => {
        this.hide()
    }

    focusHandler = () => {
        // we use `setTimeout` to make sure `selection` is already updated
        setTimeout(() => this.update(this.editor.view))
    }

    blurHandler = ({event}: { event: FocusEvent }) => {
        if (this.preventHide) {
            this.preventHide = false

            return
        }

        const tippyElement: HTMLElement | null = this.tippy ? this.tippy.popper : null
        if (event?.relatedTarget && tippyElement?.parentNode?.contains(event.relatedTarget as Node)) {
            return
        }

        this.hide()
    }

    update(view: EditorView, oldState?: EditorState) {
        const {state, composing} = view
        const {doc, selection} = state
        const isSame = oldState && oldState.doc.eq(doc) && oldState.selection.eq(selection)

        if (composing || isSame) {
            return
        }

        // support for CellSelections
        const {ranges} = selection
        const from = Math.min(...ranges.map(range => range.$from.pos))
        const to = Math.max(...ranges.map(range => range.$to.pos))

        const shouldShow = this.shouldShow({
            editor: this.editor,
            view,
            state,
            oldState,
            from,
            to,
        })

        if (!shouldShow) {
            this.hide()
            return
        }

        this.tippyGetReferenceClientRect = () => {
            if (isNodeSelection(state.selection)) {
                const node = view.nodeDOM(from) as HTMLElement

                if (node) {
                    return node.getBoundingClientRect()
                }
            }

            return posToDOMRect(view, from, to)
        };

        this.show()
    }

    show() {
        if (!this.tippy) {
            this.tippy = tippy(this.view.dom, this.tippyOptions);
        }
        if (this.tippyGetReferenceClientRect) {
            this.tippy.setProps({getReferenceClientRect: this.tippyGetReferenceClientRect})
        }

        this.updateContentSafe(this.tippy.popper)
        this.tippy.show()
    }

    updateContentSafe(element: HTMLElement) {
        if (this.updateContent) this.updateContent(element)
    }

    hide() {
        if (this.tippy) {
            this.tippy.hide()
            this.tippy.destroy()
            this.tippy = null;
        }
    }

    destroy() {
        this.hide();
        this.view.dom.removeEventListener('dragstart', this.dragstartHandler)
        this.editor.off('focus', this.focusHandler)
        this.editor.off('blur', this.blurHandler)
    }
}

export const BubbleLikeMenuPlugin = (options: BubbleLikeMenuPluginProps) => {
    return new Plugin({
        key: typeof options.key === 'string'
            ? new PluginKey(options.key)
            : options.key,
        view: view => new BubbleLikeMenuView({view, ...options}),
    })
}
